import { useEffect, useState } from 'react'
import {
  Search,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Building2,
  X,
  Users,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Project } from '../types'
import Progress from '../components/Progress'
import { useProjectsStore } from '../stores/projectsStore'
import { useAuthStore } from '../stores/authStore'
import { useTenantsStore } from '../stores/tenantsStore'
import { usersApi, type User } from '../services/usersApi'
import type { ProjectOwner } from '../services/projectsApi'

const Projects = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tenantId: '',
    status: 'TO_DO' as 'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'IN_REVIEW',
    owners: [] as string[]
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pentesters, setPentesters] = useState<User[]>([])
  const [pentestersLoading, setPentestersLoading] = useState(false)
  
  // Store hooks
  const { 
    projects, 
    loading, 
    error, 
    fetchProjects, 
    createProject,
    updateProject,
    deleteProject 
  } = useProjectsStore()
  
  const { user, selectedTenantId } = useAuthStore()
  const { tenants, fetchTenants } = useTenantsStore()
  
  // Check if user has admin role (only ADMIN can see action icons)
  const isAdmin = user?.roles?.includes('ADMIN')
  const isPentester = user?.roles?.includes('PENTESTER')
  
  useEffect(() => {
    // For pentesters, only fetch projects if a tenant is selected
    if (isPentester) {
      if (selectedTenantId) {
        fetchProjects(selectedTenantId)
      }
    } else {
      // For other users, fetch all projects
      fetchProjects()
    }
    fetchTenants()
    fetchPentesters()
  }, [fetchProjects, fetchTenants, selectedTenantId, isPentester])

  const fetchPentesters = async () => {
    setPentestersLoading(true)
    try {
      const response = await usersApi.getPentesters()
      if (response.success && response.data) {
        setPentesters(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch pentesters:', error)
    } finally {
      setPentestersLoading(false)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId && 
          !(event.target as Element).closest('.dropdown-container') &&
          !(event.target as Element).closest('.dropdown-menu')) {
        setOpenMenuId(null)
        setDropdownPosition(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMenuId])

  // Transform API data to match the existing Project interface
  const transformedProjects: Project[] = projects.map((project, index) => {
    // Map API status to display status
    const statusMap = {
      'TO_DO': 'to_do' as const,
      'IN_PROGRESS': 'in_progress' as const,
      'COMPLETED': 'completed' as const,
      'ON_HOLD': 'on_hold' as const,
      'IN_REVIEW': 'in_review' as const,
    }
    
    // Generate static data for missing fields
    const staticClients = [
      'TechCorp Solutions',
      'GlobalBank International',
      'HealthCare Plus',
      'GovTech Services',
      'FinTech Innovations',
    ]
    
    const progress = project.status === 'COMPLETED' ? 100 : 0
    
    const lastModified = new Date(project.updatedAt)
    const startDate = new Date(project.createdAt)
    
    return {
      id: project._id,
      name: project.name,
      client: staticClients[index % staticClients.length], // Static client data
      assetCount: project.assetsCount, // Static asset count
      lastModified: lastModified.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      status: statusMap[project.status] || 'to_do',
      progress: progress,
      startDate: startDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      endDate: project.status === 'COMPLETED' 
        ? new Date().toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : undefined,
    }
  })

  const filteredProjects = transformedProjects.filter(
    project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.id.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      await deleteProject(projectId)
      setOpenMenuId(null)
    }
  }

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    setIsSubmitting(true)
    
    try {
      await createProject({
        name: formData.name.trim(),
        description: formData.description.trim(),
        tenantId: formData.tenantId,
        owners: formData.owners
      })
      setShowCreateModal(false)
      setFormData({ name: '', description: '', tenantId: '', status: 'TO_DO', owners: [] })
    } catch (err) {
      console.error('Failed to create project:', err)
      setFormError('Failed to create project. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    setFormData({ name: '', description: '', tenantId: '', status: 'TO_DO', owners: [] })
    setFormError(null)
  }

  const handleEditProject = (projectId: string) => {
    const project = projects.find(p => p._id === projectId)
    if (project) {
      setEditingProjectId(projectId)
      setFormData({
        name: project.name,
        description: project.description,
        tenantId: project.tenantId,
        status: project.status,
        owners: project.owners?.map(o => o.userId) || []
      })
      setShowEditModal(true)
      setOpenMenuId(null)
      setDropdownPosition(null)
    }
  }

  const handleUpdateProject = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingProjectId) return

    setFormError(null)
    setIsSubmitting(true)

    try {
      await updateProject(editingProjectId, {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status,
        owners: formData.owners
      })
      setShowEditModal(false)
      setEditingProjectId(null)
      setFormData({ name: '', description: '', tenantId: '', status: 'TO_DO', owners: [] })
    } catch (err) {
      console.error('Failed to update project:', err)
      setFormError('Failed to update project. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setEditingProjectId(null)
    setFormData({ name: '', description: '', tenantId: '', status: 'TO_DO', owners: [] })
    setFormError(null)
  }

  const toggleOwner = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      owners: prev.owners.includes(userId)
        ? prev.owners.filter(id => id !== userId)
        : [...prev.owners, userId]
    }))
  }

  const getOwnerInitials = (owner: ProjectOwner) => {
    return `${owner.firstName.charAt(0)}${owner.lastName.charAt(0)}`.toUpperCase()
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      to_do: {
        className:
          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        text: 'To Do',
      },
      in_progress: {
        className:
          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        text: 'In Progress',
      },
      in_review: {
        className:
          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        text: 'In Review',
      },
      on_hold: {
        className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        text: 'On Hold',
      },
      completed: {
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        text: 'Completed',
      },
    }

    const config =
      statusConfig[status.toLowerCase() as keyof typeof statusConfig] || statusConfig.to_do

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}
      >
        {config.text}
      </span>
    )
  }

  // Show message for pentesters when no tenant is selected
  if (isPentester && !selectedTenantId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 dark:text-white">No Tenant Selected</p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Please select a tenant from the sidebar to view projects
          </p>
        </div>
      </div>
    )
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading projects...</p>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button 
            onClick={() => fetchProjects()}
            className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Projects
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your projects
          </p>
        </div>

        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          {isAdmin && (
            <button 
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Project
            </button>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects by name, client, or ID..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            {filteredProjects.length} of {transformedProjects.length} projects
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Assets
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Last Modified
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Owners
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 relative">
              {filteredProjects.map(project => {
                const originalProject = projects.find(p => p._id === project.id)
                return (
                <tr
                  key={project.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {project.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {project.assetCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {project.lastModified}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex -space-x-2">
                      {originalProject?.owners && originalProject.owners.length > 0 ? (
                        <>
                          {originalProject.owners.slice(0, 3).map((owner) => (
                            <div
                              key={owner.userId}
                              className="group relative inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary-600 text-white text-xs font-medium ring-2 ring-white dark:ring-gray-800 cursor-pointer"
                            >
                              {getOwnerInitials(owner)}
                              <div className="absolute bottom-full mb-2 hidden group-hover:block z-50 px-2 py-1 text-xs font-normal text-white bg-gray-900 rounded whitespace-nowrap pointer-events-none">
                                {owner.firstName} {owner.lastName}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          ))}
                          {originalProject.owners.length > 3 && (
                            <div
                              className="group relative inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium ring-2 ring-white dark:ring-gray-800 cursor-pointer"
                            >
                              +{originalProject.owners.length - 3}
                              <div className="absolute bottom-full mb-2 hidden group-hover:block z-50 px-2 py-1 text-xs font-normal text-white bg-gray-900 rounded whitespace-nowrap pointer-events-none">
                                {originalProject.owners.length - 3} more owner{originalProject.owners.length - 3 > 1 ? 's' : ''}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm">No owners</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(project.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-36">
                      <Progress value={project.progress} size="lg" showLabel />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {isAdmin && (
                      <div className="relative flex items-center justify-end dropdown-container">
                        <button
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            const rect = e.currentTarget.getBoundingClientRect()
                            setDropdownPosition({
                              top: rect.bottom + 8,
                              left: rect.left - 120
                            })
                            setOpenMenuId(prev => prev === project.id ? null : project.id)
                          }}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Portal Dropdown - moved outside table container */}
      {openMenuId && dropdownPosition && (
        <div 
          className="dropdown-menu fixed z-[9999] w-40 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          {isAdmin && (
            <>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleEditProject(openMenuId)
                }}
              >
                <Edit className="h-4 w-4" /> Edit
              </button>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                onClick={(e) => {
                  e.stopPropagation()
                  setOpenMenuId(null)
                  setDropdownPosition(null)
                  handleDeleteProject(openMenuId)
                }}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </>
          )}
        </div>
      )}


      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000] flex items-center justify-center">
          <div className="relative mx-auto p-6 border w-full max-w-lg shadow-xl rounded-lg bg-white dark:bg-gray-800">
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Create New Project
                </h3>
                <button
                  onClick={closeCreateModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  disabled={isSubmitting}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
                </div>
              )}

              <form onSubmit={handleCreateProject}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-colors"
                      placeholder="Enter project name"
                      required
                      disabled={isSubmitting}
                      minLength={3}
                      maxLength={100}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.name.length}/100 characters
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-colors"
                      rows={4}
                      placeholder="Enter project description"
                      required
                      disabled={isSubmitting}
                      minLength={10}
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.description.length}/500 characters
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tenant <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.tenantId}
                      onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-colors"
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select a tenant</option>
                      {tenants.map((tenant) => (
                        <option key={tenant._id} value={tenant._id}>
                          {tenant.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Select the tenant this project belongs to
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Users className="h-4 w-4 inline mr-1" />
                      Owners (Pentesters)
                    </label>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-700/50">
                      {pentestersLoading ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading pentesters...</p>
                      ) : pentesters.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No pentesters available</p>
                      ) : (
                        <div className="space-y-2">
                          {pentesters.map((pentester) => (
                            <label
                              key={pentester._id}
                              className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={formData.owners.includes(pentester._id)}
                                onChange={() => toggleOwner(pentester._id)}
                                disabled={isSubmitting}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {pentester.firstName} {pentester.lastName}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {pentester.email}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Select pentesters to assign as project owners
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      'Create Project'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000] flex items-center justify-center">
          <div className="relative mx-auto p-6 border w-full max-w-lg shadow-xl rounded-lg bg-white dark:bg-gray-800">
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Edit Project
                </h3>
                <button
                  onClick={closeEditModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  disabled={isSubmitting}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
                </div>
              )}

              <form onSubmit={handleUpdateProject}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-colors"
                      placeholder="Enter project name"
                      required
                      disabled={isSubmitting}
                      minLength={3}
                      maxLength={100}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.name.length}/100 characters
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-colors"
                      rows={4}
                      placeholder="Enter project description"
                      required
                      disabled={isSubmitting}
                      minLength={10}
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.description.length}/500 characters
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-colors"
                      required
                      disabled={isSubmitting}
                    >
                      <option value="TO_DO">To Do</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="IN_REVIEW">In Review</option>
                      <option value="ON_HOLD">On Hold</option>
                      <option value="COMPLETED">Completed</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Update the current status of the project
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Users className="h-4 w-4 inline mr-1" />
                      Owners (Pentesters)
                    </label>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-700/50">
                      {pentestersLoading ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading pentesters...</p>
                      ) : pentesters.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No pentesters available</p>
                      ) : (
                        <div className="space-y-2">
                          {pentesters.map((pentester) => (
                            <label
                              key={pentester._id}
                              className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={formData.owners.includes(pentester._id)}
                                onChange={() => toggleOwner(pentester._id)}
                                disabled={isSubmitting}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {pentester.firstName} {pentester.lastName}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {pentester.email}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Select pentesters to assign as project owners
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tenant
                    </label>
                    <div className="flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900">
                      <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600 dark:text-gray-400">
                        {tenants.find(t => t._id === formData.tenantId)?.name || 'N/A'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Tenant assignment cannot be changed after creation
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      'Update Project'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default Projects
